import React, { useState, useEffect, useCallback } from 'react';
import Button from '../components/tools/Button';
import { fetchBoxData } from '../../core/services/boxService';
import { BoxData } from '../../core/models/Box';

interface ErrorPageProps {
  code?: number | string;
  message?: string;
  onRetry?: () => void;
}

const retryIntervals = [5, 10, 30, 60, 300];

const ErrorPage: React.FC<ErrorPageProps> = ({
  code = 404,
  message = 'Lo sentimos, ocurrió un error al cargar el WOD.',
  onRetry,
}) => {
  const [attemptIndex, setAttemptIndex] = useState(0);
  const [timeLeft, setTimeLeft] = useState(retryIntervals[0]);
  const [buttonVisible, setButtonVisible] = useState(true);
  const [boxInfo, setBoxInfo] = useState<BoxData | null>(null);

  // Carga datos del box
  useEffect(() => {
    fetchBoxData('2').then(setBoxInfo).catch(console.error);
  }, []);

  const handleRetry = useCallback(() => {
    onRetry?.();
    const next = attemptIndex + 1;
    if (next < retryIntervals.length) {
      setAttemptIndex(next);
      setTimeLeft(retryIntervals[next]);
    } else {
      setAttemptIndex(next);
      setButtonVisible(false);
    }
  }, [attemptIndex, onRetry]);

  useEffect(() => {
    if (!buttonVisible) return;
    if (timeLeft === 0) {
      handleRetry();
    } else {
      const timer = setTimeout(() => setTimeLeft(timeLeft - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [timeLeft, buttonVisible, handleRetry]);

  useEffect(() => {
    if (attemptIndex >= retryIntervals.length) {
      setButtonVisible(false);
    }
  }, [attemptIndex]);

  // Helper
  const formatTime = (sec: number) => (sec < 60 ? `${sec}s` : `${Math.floor(sec / 60)}m`);

  return (
    <div className="flex flex-col w-full h-full min-h-screen bg-gradient-to-b from-black/60 to-black text-white p-6 lg:p-12">
      {/* HEADER */}
      <header className="flex flex-col items-center mb-8">
        {boxInfo && (
          <>
            <img src="/assets/logo/logo.png" alt="Alarma" className="w-32 h-10" />

            <span
              className={`px-3 py-0 rounded-full text-sm mb-0 mt-0 ${boxInfo.is_open ? 'bg-white-500' : 'bg-red-500'}`}
            >
              {boxInfo.is_open ? 'ABIERTO' : 'CERRADO'}
            </span>
          </>
        )}
      </header>

      {/* BODY */}
      <main className="flex-grow flex flex-col items-center justify-center text-center space-y-4">
        <span className="text-6xl md:text-8xl font-extrabold drop-shadow-lg">{code}</span>
        {boxInfo && (
          <div className="text-2xl md:text-4xl font-bold drop-shadow-md mb-2">{boxInfo!.name}</div>
        )}
        <p className="text-lg md:text-2xl font-medium drop-shadow-md max-w-xl">{message}</p>
        {boxInfo && (
          <p className="text-sm md:text-base opacity-75">
            Contacto soporte: {boxInfo.support_email}
          </p>
        )}

        {buttonVisible && (
          <div className="flex flex-col items-center mt-4">
            <Button
              onClick={handleRetry}
              disabled={timeLeft > 0}
              className="px-8 py-2 rounded-2xl border border-white bg-transparent text-white transition-all disabled:opacity-50"
            >
              Reintentar {timeLeft > 0 && `(${formatTime(timeLeft)})`}
            </Button>
            <p className="mt-2 text-sm opacity-75">
              Intento {Math.min(attemptIndex + 1, retryIntervals.length)}/{retryIntervals.length}
            </p>
          </div>
        )}
      </main>

      {/* FOOTER */}
      <footer className="mt-8 text-center text-xs opacity-50">
        <p>Powered by MonkeyBox App</p>
      </footer>
    </div>
  );
};

export default ErrorPage;
